/**
 * Peer - Clean DX wrapper for peer-to-peer connections
 *
 * Provides a simple interface for connecting to a peer by tags/publicKey,
 * with automatic reconnection and message buffering.
 */
import { EventEmitter } from 'eventemitter3';
import { AnswererConnection } from '../connections/answerer.js';
import { ConnectionState } from '../connections/events.js';
/**
 * Peer - A clean interface for peer-to-peer connections
 *
 * @example
 * ```typescript
 * const peer = await rondevu.peer({
 *   publicKey: 'abc123...',
 *   tags: ['chat']
 * })
 *
 * peer.on('open', () => {
 *   console.log('Connected to', peer.peerPublicKey)
 *   peer.send('Hello!')
 * })
 *
 * peer.on('message', (data) => {
 *   console.log('Received:', data)
 * })
 *
 * peer.on('state', (state) => {
 *   console.log('Connection state:', state)
 * })
 *
 * // Access underlying WebRTC objects
 * const pc = peer.peerConnection  // RTCPeerConnection
 * const dc = peer.dataChannel     // RTCDataChannel
 * ```
 */
export class Peer extends EventEmitter {
    constructor(options) {
        super();
        this.connection = null;
        this._state = 'connecting';
        this._peerPublicKey = '';
        this._offerId = '';
        this.api = options.api;
        this.tags = options.tags;
        this.targetPublicKey = options.publicKey;
        this.iceServers = options.iceServers;
        this.iceTransportPolicy = options.iceTransportPolicy;
        this.webrtcAdapter = options.webrtcAdapter;
        this.connectionConfig = options.config;
        this.debugEnabled = options.debug || false;
    }
    /**
     * Initialize the peer connection (called internally by Rondevu.peer())
     */
    async initialize() {
        this.debug('Initializing peer connection');
        this.debug(`Tags: ${this.tags.join(', ')}${this.targetPublicKey ? `, publicKey: ${this.targetPublicKey}` : ''}`);
        // Discover offers
        const result = (await this.api.discover({
            tags: this.tags,
            limit: 100,
        }));
        if (!result.offers || result.offers.length === 0) {
            throw new Error(`No peers found for tags: ${this.tags.join(', ')}`);
        }
        // Filter by publicKey if specified
        let availableOffers = result.offers;
        if (this.targetPublicKey) {
            availableOffers = result.offers.filter((o) => o.publicKey === this.targetPublicKey);
            if (availableOffers.length === 0) {
                throw new Error(`No peers found for tags: ${this.tags.join(', ')} from ${this.targetPublicKey}`);
            }
        }
        // Pick a random offer
        const offer = availableOffers[Math.floor(Math.random() * availableOffers.length)];
        this._peerPublicKey = offer.publicKey;
        this._offerId = offer.offerId;
        this.debug(`Selected offer ${offer.offerId} from ${offer.publicKey}`);
        // Create the underlying AnswererConnection
        this.connection = new AnswererConnection({
            api: this.api,
            ownerPublicKey: offer.publicKey,
            tags: offer.tags,
            offerId: offer.offerId,
            offerSdp: offer.sdp,
            rtcConfig: {
                iceServers: this.iceServers,
                iceTransportPolicy: this.iceTransportPolicy,
            },
            webrtcAdapter: this.webrtcAdapter,
            config: {
                ...this.connectionConfig,
                debug: this.debugEnabled,
            },
            matchedTags: this.tags, // Pass the tags we used to discover this offer
        });
        // Wire up events
        this.setupEventHandlers();
        // Start connection
        await this.connection.initialize();
    }
    /**
     * Setup event handlers to forward from AnswererConnection
     */
    setupEventHandlers() {
        if (!this.connection)
            return;
        // Map ConnectionState to PeerState
        this.connection.on('state:changed', ({ oldState, newState, reason }) => {
            const mappedOld = this.mapState(oldState);
            const mappedNew = this.mapState(newState);
            if (mappedOld !== mappedNew) {
                this._state = mappedNew;
                this.emit('state', mappedNew, mappedOld);
            }
        });
        // Forward connection events
        this.connection.on('connected', () => {
            this._state = 'connected';
            this.emit('open');
        });
        this.connection.on('closed', reason => {
            this._state = 'closed';
            this.emit('close', reason);
        });
        this.connection.on('failed', error => {
            this._state = 'failed';
            this.emit('error', error);
        });
        // Forward message events
        this.connection.on('message', data => {
            this.emit('message', data);
        });
        // Forward reconnection events
        this.connection.on('reconnect:scheduled', info => {
            this._state = 'reconnecting';
            this.emit('reconnecting', info.attempt, info.maxAttempts);
        });
        this.connection.on('reconnect:success', () => {
            // State will be updated by 'connected' event
        });
        this.connection.on('reconnect:failed', error => {
            this.emit('error', error);
        });
    }
    /**
     * Map internal ConnectionState to simplified PeerState
     */
    mapState(state) {
        switch (state) {
            case ConnectionState.INITIALIZING:
            case ConnectionState.GATHERING:
            case ConnectionState.SIGNALING:
            case ConnectionState.CHECKING:
            case ConnectionState.CONNECTING:
                return 'connecting';
            case ConnectionState.CONNECTED:
                return 'connected';
            case ConnectionState.DISCONNECTED:
                return 'disconnected';
            case ConnectionState.RECONNECTING:
                return 'reconnecting';
            case ConnectionState.FAILED:
                return 'failed';
            case ConnectionState.CLOSED:
                return 'closed';
            default:
                return 'connecting';
        }
    }
    // ========================================
    // Public Properties
    // ========================================
    /**
     * Current connection state
     */
    get state() {
        return this._state;
    }
    /**
     * Public key of the connected peer
     */
    get peerPublicKey() {
        return this._peerPublicKey;
    }
    /**
     * The offer ID being used for this connection
     */
    get offerId() {
        return this._offerId;
    }
    /**
     * Tags used for discovery
     */
    get peerTags() {
        return this.tags;
    }
    /**
     * The underlying RTCPeerConnection (null if not connected)
     */
    get peerConnection() {
        return this.connection?.getPeerConnection() ?? null;
    }
    /**
     * The underlying RTCDataChannel (null if not connected)
     */
    get dataChannel() {
        return this.connection?.getDataChannel() ?? null;
    }
    /**
     * Whether the peer is currently connected
     */
    get isConnected() {
        return this._state === 'connected';
    }
    // ========================================
    // Public Methods
    // ========================================
    /**
     * Send a message to the peer
     * Messages are buffered if not connected (when buffering is enabled)
     *
     * @param data - String, ArrayBuffer, or Blob to send
     */
    send(data) {
        if (!this.connection) {
            throw new Error('Peer not initialized');
        }
        this.connection.send(data);
    }
    /**
     * Close the peer connection
     */
    close() {
        this.debug('Closing peer connection');
        this.connection?.close();
        this._state = 'closed';
    }
    /**
     * Get the underlying AnswererConnection for advanced use cases
     */
    getConnection() {
        return this.connection;
    }
    /**
     * Debug logging
     */
    debug(...args) {
        if (this.debugEnabled) {
            console.log('[Peer]', ...args);
        }
    }
}
